import { type FullBuyerMaterialSpecification, isFieldErrorResponse, MaterialComponentCategory } from 'api';
import { convertFromMaterialComponentValue, convertToMaterialComponentInput, useNotification } from 'features';
import { Form, useForm, yupResolver } from 'form';
import { useTranslation } from 'i18n';
import { useNavigate } from 'react-router-dom';
import { Box, Button } from 'ui';
import { DeleteOutlined } from 'ui/icons';

import { useDeleteBuyerMaterialSpecification } from '../../hooks/useDeleteBuyerMaterialSpecification/useDeleteBuyerMaterialSpecification';
import { useUpdateBuyerMaterialSpecification } from '../../hooks/useUpdateBuyerMaterialSpecification/useUpdateBuyerMaterialSpecification';
import {
  type BuyerMaterialSpecificationFormInputType,
  type BuyerMaterialSpecificationFormType,
  BuyerMaterialSpecificationSchema,
} from '../../pages/schema';
import { BuyerMaterialSpecificationFormFields } from '../BuyerMaterialSpecificationFormFields/BuyerMaterialSpecificationFormFields';

type Props = {
  buyerMaterialSpecificationId: number;
  buyerMaterialSpecificationData: FullBuyerMaterialSpecification;
};

export const BuyerMaterialSpecificationUpdateForm = ({
  buyerMaterialSpecificationId,
  buyerMaterialSpecificationData,
}: Props) => {
  const navigate = useNavigate();
  const { t } = useTranslation('orders');
  const { open } = useNotification();
  const form = useForm<BuyerMaterialSpecificationFormInputType, unknown, BuyerMaterialSpecificationFormType>({
    resolver: yupResolver(BuyerMaterialSpecificationSchema),
    values: {
      buyer_id: buyerMaterialSpecificationData.buyer.id,
      document: undefined,
      file_url: buyerMaterialSpecificationData.file_url ?? undefined,
      material_colour_id: buyerMaterialSpecificationData.material_colour?.id ?? undefined,
      material_form_id: buyerMaterialSpecificationData.material_form.id,
      material_grade_id: buyerMaterialSpecificationData.material_grade.id,
      material_id: buyerMaterialSpecificationData.material.id,
      material_qualities: convertFromMaterialComponentValue(
        buyerMaterialSpecificationData.material_component_values,
        MaterialComponentCategory.MATERIAL_QUALITY
      ),
      notes: buyerMaterialSpecificationData.notes || undefined,
      packaging_specification_ids: buyerMaterialSpecificationData.packaging_specifications.map(
        (packagingSpecification) => packagingSpecification.id
      ),
    },
  });

  const onUpdateError = (error: Error) => {
    if (isFieldErrorResponse(error)) {
      for (const field of Object.keys(error.response?.data)) {
        form.setError(field as keyof BuyerMaterialSpecificationFormType, { message: error.response.data[field] });
      }
    } else {
      open({
        message: t('buyerMaterialSpecifications.updateForm.notifications.updateFailed', {
          values: { error: String(error) },
        }),
        type: 'error',
      });
    }
  };
  const { updateBuyerMaterialSpecification, isPending } = useUpdateBuyerMaterialSpecification({
    onError: onUpdateError,
  });
  const { mutate: deleteBuyerMaterialSpecification } = useDeleteBuyerMaterialSpecification();
  const onUpdate = async (values: BuyerMaterialSpecificationFormType) => {
    const materialComponents = convertToMaterialComponentInput(values.material_qualities);

    await updateBuyerMaterialSpecification({
      buyerId: values.buyer_id,
      buyerMaterialSpecificationId,
      document: values.document ?? undefined,
      materialColourId: values.material_colour_id,
      materialComponents,
      materialFormId: values.material_form_id,
      materialGradeId: values.material_grade_id,
      materialId: values.material_id,
      notes: values.notes,
      packagingSpecificationIds: values.packaging_specification_ids,
    });
  };

  const onDelete = () => {
    deleteBuyerMaterialSpecification({ buyerMaterialSpecificationId: Number(buyerMaterialSpecificationId) });
  };

  return (
    <Form<BuyerMaterialSpecificationFormInputType, unknown, BuyerMaterialSpecificationFormType>
      {...form}
      onSubmit={onUpdate}
    >
      <BuyerMaterialSpecificationFormFields />
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
        }}
      >
        <Button onClick={onDelete} variant="secondary" startIcon={<DeleteOutlined />}>
          {t('buyerMaterialSpecifications.updateForm.actions.delete')}
        </Button>
        <Box
          sx={{
            display: 'flex',
            gap: 2,
          }}
        >
          <Button onClick={() => navigate(-1)} variant="secondary">
            {t('buyerMaterialSpecifications.updateForm.actions.cancel')}
          </Button>
          <Button loading={isPending} type="submit">
            {t('buyerMaterialSpecifications.updateForm.actions.update')}
          </Button>
        </Box>
      </Box>
    </Form>
  );
};
